import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ClipboardCheck, Download, ExternalLink, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { STATUS_LABELS, fmtEUR } from "./approvalsConstants";
import { safeDownloadBlob } from "../../lib/downloadUtils";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export function InitiativeApprovalsCard({ initiativeId, token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!initiativeId) return;
    fetch(`${API}/api/initiatives/${initiativeId}/approvals`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initiativeId, token]);

  const downloadPdf = async (item) => {
    try {
      await safeDownloadBlob(
        `${API}/api/approvals/${item.id}/pdf`,
        `Aprobacion_${item.project_name.replace(/\s+/g, "_")}.pdf`,
        token
      );
    } catch (e) {
      toast.error(e.message || "Error al descargar");
    }
  };

  return (
    <Card data-testid="initiative-approvals-card">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-base">
          <ClipboardCheck className="w-4 h-4 text-primary" />
          Fichas de aprobación
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => navigate("/approvals")}>
          <ExternalLink className="w-3.5 h-3.5 mr-1" />Ir a Aprobaciones
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground py-2">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="py-6 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-3">
              Este proyecto aún no tiene fichas de aprobación vinculadas.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate("/approvals")}>
              Crear ficha de aprobación
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const info = STATUS_LABELS[it.status] || STATUS_LABELS.draft;
              return (
                <div
                  key={it.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border hover:border-primary/30 transition"
                  data-testid={`initiative-approval-${it.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{it.project_name}</span>
                      <Badge variant="outline" className={info.color}>
                        {info.label}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {fmtEUR(it.presupuesto_total)} ·{" "}
                      {it.director?.signed_at
                        ? `Firmada ${it.director.signed_at.slice(0, 10)} por ${it.director.name}`
                        : `Creada ${it.created_at?.slice(0, 10) || "—"}`}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => downloadPdf(it)} data-testid={`download-approval-${it.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" />PDF
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
